import axios, { AxiosError } from 'axios';
import { auth } from '../firebase'; // your firebase.ts file

interface ApiError {
  message?: string;
  error?: string;
}

export const backendAPI = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  withCredentials: true,
});


interface Business {
  id: number;
  name: string;
  registryId: string;
  status: string;
  location: string;
  businessId: number;
  userId: number;
  createdAt: string;
  updatedAt: string;
  templateId?: number | null;
  logo?: string | null;
  details?: {
    description?: string;
    investment_level?: string;
    sector?: string;
    scalability?: string;
    economic_activity?: string;
    primary_color?: string;
    secondary_color?: string;
    font?: string;
  };
}

export const get_website_recommendation = async (
  business: Business,
  onToken: (token: string) => void
) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not logged in");

    const idToken = await user.getIdToken();

    const response = await fetch(`${process.env.REACT_APP_API_URL}businesses/website_recommendation`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({
        business: {
          name: business.name,
          description: business.details?.description,
        },
      }),

    });

    if (!response.body) {
      throw new Error("No response body");
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder("utf-8");

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      const chunk = decoder.decode(value, { stream: true });
      onToken(chunk);
    }
  } catch (error: any) {
    console.error(error);
    throw new Error("Get website recommendation failed");
  }
};

export const generate_website = async (business: Business) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not logged in");
    const idToken = await user.getIdToken();

    const response = await backendAPI.post(
      'businesses/generate_website',
      {business},
      {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.message || 'Crete website failed'
    );
  }
};

export type WebsiteRegenerationScope =
  | "layout"
  | "theme"
  | "content"
  | "images"
  | "full";

export const regenerate_website = async (
  businessId: number,
  scope: WebsiteRegenerationScope
) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not logged in");
    const idToken = await user.getIdToken();

    const response = await backendAPI.post(
      "businesses/website/regenerate",
      { businessId, scope },
      {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      }
    );

    return response.data;
  } catch (error: any) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.message
        || (error as AxiosError<ApiError>)?.response?.data?.error
        || "Website regeneration failed"
    );
  }
};

export const get_business_website  = async (businessId: number) => {
  try {
    const response = await backendAPI.get('businesses/business_website', {
      params: {
        userBusinessId: businessId,
      },
    });
    return response.data;
  } catch (error: any) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.message || 'Get business checklist failed'
    );
  }
};

export const patch_website_content = async (
  businessId: number,
  slots: Record<string, string>
) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not logged in");

    const idToken = await user.getIdToken();

    const response = await backendAPI.patch(
      "/businesses/website/content",
      {
        businessId,
        slots,
      },
      {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      }
    );

    return response.data.content_v2;
  } catch (error: any) {
    throw new Error(
      error?.response?.data?.message || "Patch website content failed"
    );
  }
};

export const resolve_domain = async (domain: string) => {
  try {
    const response = await backendAPI.get('businesses/resolve-domain', {
      params: { domain },
    });
    return response.data;
  } catch (error: any) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.message || 'Domain resolution failed'
    );
  }
};

export const check_if_website_owner = async (businessId: number) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not logged in");

    const idToken = await user.getIdToken();

    const response = await backendAPI.get('businesses/check_if_website_owner', {
      params: { userBusinessId: businessId },
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
    });

    return response.data;

  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      console.error("Axios error response:", error.response);
    } else {
      console.error("Non-Axios error:", error);
    }

    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.message || 'Check website ownership failed'
    );
  }
};

export const add_custom_domain = async (businessId: number, domain: string) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not logged in");
    const idToken = await user.getIdToken();

    const response = await backendAPI.post(
      `businesses/${businessId}/custom-domain`,
      { domain },
      { headers: { Authorization: `Bearer ${idToken}` } }
    );
    return response;
  } catch (err: any) {
    console.error(err);
    return { success: false, error: err.response?.data?.message || "Server error" };
  }
};

export const delete_custom_domain = async (
  businessId: number,
  domain: string
) => {
  const user = auth.currentUser;
  if (!user) throw new Error("User not logged in");

  const idToken = await user.getIdToken();

  const response = await backendAPI.delete(
    `businesses/${businessId}/custom-domain`,
    {
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
      data: {
        domain, // axios requires `data` for DELETE bodies
      },
    }
  );

  return response; // ✅ SOLO AxiosResponse
};


export const get_custom_domain_status = async (businessId: number, domain: string) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not logged in");
    const idToken = await user.getIdToken();
    const response = await backendAPI.get("/businesses/custom-domain-status", {
      params: { businessId, domain },
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
    });
    return response.data;
  } catch (err) {
    console.error(err);
    return { success: false, status: "error" };
  }
};

export const delete_website = async (businessId: number, businessName: string) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not logged in");

    const idToken = await user.getIdToken();

    const response = await backendAPI.delete(
      `businesses/${businessId}/website`,
      {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
        data: {
          businessName,
        },
      }
    );

    return response.data;
  } catch (error: any) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.message
        || (error as AxiosError<ApiError>)?.response?.data?.error
        || "Delete website failed"
    );
  }
};

export const get_business_website_url  = async (businessId: number) => {
  try {
    const response = await backendAPI.get('businesses/business_website_url', {
      params: {
        userBusinessId: businessId,
      },
    });
    return response;
  } catch (error: any) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.message || 'Get business checklist failed'
    );
  }
};